/**
 * The `discount.*` capabilities (CAPABILITIES.md §3.4). Ids from the cart contract (P0 step 12);
 * the permission rules below are R2.6b's (decision 66). A control is drawn by asking every id that
 * guards it: removing a discount from a printed bill asks `discount.remove` and then
 * `discount.editAfterPrint`. The sheet's other conditions (cart has items, channel, balance) are
 * the page's and arrive with the discount v2 flow (N23).
 */
import { definePolicies, definePolicy } from "../engine";
import { permission, when } from "../requirements";
import type { Facts } from "../types";
import type { OrderResource } from "./order";

declare module "../types" {
  interface CapabilityMap {
    /** open the deals board: deals, coupons and partner offers */
    "discount.apply": OrderResource;
    /** take an applied discount, deal or coupon off the order */
    "discount.remove": OrderResource;
    /** change an applied discount's rate or reason */
    "discount.override": OrderResource;
    /** change any discount of a bill already printed (asked besides the id of the change itself) */
    "discount.editAfterPrint": OrderResource;
    /** ask a manager to approve the order's discount */
    "discount.requestApproval": OrderResource;
  }
}

/** named, so the requirement is explained as `when(billPrinted):permission:…` */
const billPrinted = (_facts: Facts, order: OrderResource) => order.billPrinted;

/** a kiosk has no cashier: its customer applies their own offer, so no operator permission is asked (the old kiosk asked none; R2.7a) */
const notKiosk = (facts: Facts) => !facts.env.kiosk;

/** the backend does not check `APPLY_DISCOUNT` on save (only the percentage limit), so the frontend is the only gate (T2) */
const APPLY_POLICY: Parameters<typeof definePolicy<OrderResource>>[1] = {
  enabledWhen: [when(notKiosk, permission("APPLY_DISCOUNT"))],
  server: "not enforced",
};

export const DISCOUNT_POLICIES = definePolicies("discount", [
  definePolicy<OrderResource>("discount.apply", APPLY_POLICY),
  // the old FE's `onDeleteTap` asks the same permission as applying (decision 66); v1 never checks it
  definePolicy<OrderResource>("discount.remove", APPLY_POLICY),
  definePolicy<OrderResource>("discount.override", { enabledWhen: [permission("OVERRIDE_DISCOUNT")], server: "unknown" }),
  // stricter than v1, which defines the permission and reads it nowhere (decision 66)
  definePolicy<OrderResource>("discount.editAfterPrint", {
    enabledWhen: [when(billPrinted, permission("EDIT_COUPONS_OFFERS_AFTER_BILL_PRINT"))],
    server: "unknown",
  }),
]);
